/**
 * Gruppierung der OpenMapTiles-POI-Klassen in verständliche Themen.
 * Unbekannte Klassen landen automatisch in "Weitere".
 */
export const POI_THEMES = [
  {
    id: 'food',
    label: 'Essen & Trinken',
    color: '#C2562E',
    classes: ['restaurant', 'fast_food', 'cafe', 'bar', 'pub', 'biergarten', 'ice_cream', 'bakery', 'confectionery', 'beer', 'wine', 'nightclub', 'beverages']
  },
  {
    id: 'shopping',
    label: 'Einkaufen',
    color: '#9A6A2E',
    classes: ['shop', 'supermarket', 'convenience', 'grocery', 'department_store', 'marketplace', 'clothing_store', 'shoe', 'bag', 'jewelry_store', 'florist', 'furniture', 'hardware', 'book', 'gift', 'toys', 'stationery', 'newsagent', 'mobile_phone', 'computer', 'electronics', 'alcohol_shop', 'butcher', 'chemist', 'optician', 'perfumery', 'hairdresser', 'beauty', 'laundry', 'garden_centre', 'outdoor', 'sports', 'art', 'music', 'video', 'copyshop', 'travel_agency', 'estate_agent', 'car', 'car_parts', 'car_repair', 'motorcycle', 'pet']
  },
  {
    id: 'health',
    label: 'Gesundheit',
    color: '#B23B4E',
    classes: ['hospital', 'clinic', 'pharmacy', 'doctors', 'dentist', 'veterinary']
  },
  {
    id: 'education',
    label: 'Bildung',
    color: '#4A6FA5',
    classes: ['school', 'college', 'university', 'library', 'kindergarten']
  },
  {
    id: 'transport',
    label: 'Verkehr',
    color: '#4E6B57',
    classes: ['bus', 'railway', 'rail', 'tram', 'subway', 'aerialway', 'airport', 'aerodrome', 'ferry_terminal', 'harbor', 'parking', 'fuel', 'car_rental', 'car_sharing', 'charging_station', 'taxi']
  },
  {
    id: 'leisure',
    label: 'Freizeit & Kultur',
    color: '#3F7F5C',
    classes: ['attraction', 'museum', 'art_gallery', 'theatre', 'cinema', 'zoo', 'playground', 'pitch', 'stadium', 'swimming_pool', 'golf', 'park', 'picnic_site', 'camp_site', 'viewpoint', 'water_park', 'theme_park', 'dog_park', 'garden', 'sports_centre', 'fitness_centre', 'monument', 'castle', 'ruins']
  },
  {
    id: 'lodging',
    label: 'Übernachten',
    color: '#8A5BA8',
    classes: ['hotel', 'lodging', 'hostel', 'motel', 'guest_house', 'alpine_hut', 'wilderness_hut', 'caravan_site']
  },
  {
    // Velo hat ein eigenes Thema, weil man es als Ganzes ein- und ausschaltet,
    // wenn man eine Fahrt plant. Die Klassen standen vorher verstreut unter
    // Einkaufen und Verkehr.
    //
    // Trinkwasser steht weiterhin unter "Öffentlich & Service": eine Klasse
    // kann nur zu einem Thema gehören, und Brunnen sucht nicht nur, wer Velo
    // fährt.
    id: 'velo',
    label: 'Velo',
    color: '#0A7E8C',
    classes: ['bicycle', 'bicycle_parking', 'bicycle_rental', 'bicycle_share', 'cycle_barrier']
  },
  {
    id: 'public',
    label: 'Öffentlich & Service',
    color: '#5F6B78',
    classes: ['bank', 'atm', 'post', 'police', 'fire_station', 'townhall', 'embassy', 'courthouse', 'prison', 'place_of_worship', 'cemetery', 'toilets', 'drinking_water', 'information', 'telephone', 'shelter', 'recycling', 'waste_basket', 'bench', 'office', 'lawyer']
  }
];

export const OTHER_THEME = { id: 'other', label: 'Weitere', color: '#8A8578', classes: [] };

const index = new Map();
for (const t of POI_THEMES) for (const c of t.classes) index.set(c, t.id);

export function themeOf(poiClass) {
  return index.get(poiClass) || OTHER_THEME.id;
}

/** Hübscher deutscher Name für häufige Klassen, sonst Klassenname aufgeräumt */
const LABELS = {
  restaurant: 'Restaurant', fast_food: 'Schnellimbiss', cafe: 'Café', bar: 'Bar', pub: 'Pub',
  bakery: 'Bäckerei', ice_cream: 'Eisdiele', shop: 'Geschäft', supermarket: 'Supermarkt',
  convenience: 'Quartierladen', grocery: 'Lebensmittel', department_store: 'Warenhaus',
  marketplace: 'Markt', clothing_store: 'Kleider', butcher: 'Metzgerei', florist: 'Blumen',
  hospital: 'Spital', clinic: 'Klinik', pharmacy: 'Apotheke', doctors: 'Arztpraxis',
  dentist: 'Zahnarzt', veterinary: 'Tierarzt', school: 'Schule', college: 'Fachschule',
  university: 'Universität', library: 'Bibliothek', kindergarten: 'Kindergarten',
  bus: 'Bushaltestelle', railway: 'Bahnhof', rail: 'Bahnhof', tram: 'Tramhaltestelle',
  aerialway: 'Bergbahn', airport: 'Flughafen', parking: 'Parkplatz', fuel: 'Tankstelle',
  charging_station: 'Ladestation',
  bicycle: 'Velogeschäft', bicycle_parking: 'Veloparkplatz', bicycle_rental: 'Veloverleih',
  bicycle_share: 'Veloverleih', cycle_barrier: 'Umlaufsperre', attraction: 'Sehenswürdigkeit', museum: 'Museum',
  art_gallery: 'Galerie', theatre: 'Theater', cinema: 'Kino', playground: 'Spielplatz',
  pitch: 'Sportplatz', stadium: 'Stadion', swimming_pool: 'Schwimmbad', viewpoint: 'Aussichtspunkt',
  picnic_site: 'Picknickplatz', camp_site: 'Campingplatz', hotel: 'Hotel', lodging: 'Unterkunft',
  hostel: 'Hostel', alpine_hut: 'Berghütte', bank: 'Bank', atm: 'Bancomat', post: 'Post',
  police: 'Polizei', fire_station: 'Feuerwehr', townhall: 'Gemeindehaus',
  place_of_worship: 'Kirche / Religion', cemetery: 'Friedhof', toilets: 'WC',
  drinking_water: 'Trinkwasser', information: 'Information', bicycle: 'Velo',
  bicycle_rental: 'Velovermietung', car: 'Auto', car_repair: 'Autowerkstatt'
};

export function classLabel(poiClass) {
  return LABELS[poiClass] || poiClass.replace(/_/g, ' ').replace(/^\w/, (m) => m.toUpperCase());
}

/**
 * Umgekehrter Weg: vom getippten Wort zu den Klassen.
 *
 * Gespeist aus denselben Bezeichnungen, die auch im Filter stehen - plus ein
 * paar Wörter, die man beim Suchen eher tippt als die amtliche Benennung.
 * Mehrzahl wird mitgeführt, weil niemand "Restaurant" eintippt, wenn er alle
 * sucht.
 */
const SYNONYME = {
  restaurant: ['restaurants', 'essen', 'beiz', 'beizen', 'gaststätte', 'wirtschaft'],
  cafe: ['cafes', 'kaffee', 'kafi'],
  fast_food: ['imbiss', 'takeaway', 'döner', 'burger'],
  bakery: ['bäcker', 'bäckereien'],
  bar: ['bars', 'kneipe'],
  supermarket: ['supermärkte', 'einkaufen', 'migros', 'coop', 'lebensmittel'],
  pharmacy: ['apotheken'],
  doctors: ['arzt', 'ärzte', 'praxis'],
  hospital: ['spitäler', 'krankenhaus', 'notfall'],
  fuel: ['tankstellen', 'tanken', 'benzin'],
  parking: ['parkplätze', 'parken', 'parkhaus', 'parkhäuser'],
  charging_station: ['ladestationen', 'laden', 'elektro'],
  hotel: ['hotels', 'übernachten', 'schlafen'],
  atm: ['bancomaten', 'geldautomat'],
  toilets: ['toilette', 'toiletten', 'klo'],
  drinking_water: ['brunnen', 'wasser'],
  viewpoint: ['aussicht', 'aussichtspunkte'],
  museum: ['museen'],
  playground: ['spielplätze'],
  swimming_pool: ['bad', 'baden', 'schwimmbäder'],
  alpine_hut: ['berghütten', 'hütte', 'hütten', 'sac'],
  railway: ['bahnhöfe', 'zug', 'sbb'],
  bus: ['bushaltestellen', 'bus'],
  place_of_worship: ['kirchen', 'kirche', 'kapelle']
};

const SUCHINDEX = (() => {
  const idx = [];
  for (const [cls, label] of Object.entries(LABELS)) {
    idx.push({ wort: label.toLowerCase(), label, classes: [cls] });
  }
  for (const [cls, woerter] of Object.entries(SYNONYME)) {
    for (const w of woerter) {
      idx.push({ wort: w, label: LABELS[cls] || cls, classes: [cls] });
    }
  }
  // Ganze Themen: "Gesundheit", "Verkehr" usw.
  for (const t of POI_THEMES) {
    idx.push({ wort: t.label.toLowerCase(), label: t.label, classes: t.classes });
  }
  return idx;
})();

/**
 * Passt der eingetippte Text auf eine Kategorie? Vorne beginnende Treffer
 * zaehlen mehr als irgendwo enthaltene, damit "bar" nicht "Bibliothek" findet.
 */
export function kategorieFinden(text) {
  const q = String(text || '').trim().toLowerCase();
  if (q.length < 3) return null;
  let best = null;
  for (const e of SUCHINDEX) {
    let rang = null;
    if (e.wort === q) rang = 0;
    else if (e.wort.startsWith(q)) rang = 1;
    // Nur eine Endung darf drankleben ("restaurants" zu "restaurant"), kein
    // zweites Wort: "coop" ist ein Synonym fuer Supermarkt, "coop luzern"
    // aber eine Ortssuche - und wurde bisher als Kategorie verschluckt.
    else if (q.startsWith(e.wort) && !q.slice(e.wort.length).includes(' ')) rang = 2;
    if (rang === null) continue;
    if (!best || rang < best.rang || (rang === best.rang && e.classes.length > best.classes.length)) {
      best = { ...e, rang };
    }
  }
  return best ? { label: best.label, classes: best.classes } : null;
}
